import React from 'react';
import { Badge, Col, ColProps, Row, Space } from 'antd';
import isEqual from 'lodash/isEqual';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  DeliveredProcedureOutlined,
  FormOutlined,
  GlobalOutlined,
  LinkOutlined,
  MailOutlined,
} from '@ant-design/icons';

import { LeadModel } from '@/typings/models/Lead';
import { F } from '@/utils/intl';
import PrettyDate from '@/components/PrettyDate';

// region Lead Components
import LeadName from '@/components/Lead/LeadName';
import LeadEmail from '@/components/Lead/LeadEmail';
import LeadLocale from '@/components/Lead/LeadLocale';
import LeadCreatedAt from '@/components/Lead/LeadCreatedAt';
import LeadNote from '@/components/Lead/LeadNote';
// endregion
import styles from '@/components/Lead/index.less';
import classNames from 'classnames';
import CustomerState from './CustomerState';
import {
  CUSTOMER_PROPERTIES,
  CUSTOMER_PROPERTY_COLUMN_SETTINGS,
  INITIAL_PROPERTY_VISIBILITY_SETTINGS,
} from './constants';
import CustomerPhone from './CustomerPhone';
import OrderVolume from './OrderVolume';
import UtmMedium from './UtmMedium';
import UtmCampaign from './UtmCampaign';
import ResultLink from './ResultLink';
import { EuroSymbolIcon } from '@/components/Icons/CustomReactIcons';

const CustomerSubscriber = ({ lead }: { lead: LeadModel }) => {
  const isSubscriber = lead?.customer?.subscriber === true || lead?.customer?.subscriber === 1;
  return <Badge color={isSubscriber ? 'green' : 'red'} />;
};

const CustomerDoiDate = ({ lead }: { lead: LeadModel }) => {
  const doiDate = lead?.customer?.doi_date;
  if (!doiDate) return <span>-</span>;
  return <PrettyDate date={doiDate} />;
};

const LEAD_PROPERTY_ICONS = {
  [CUSTOMER_PROPERTIES.NAME]: null,
  [CUSTOMER_PROPERTIES.EMAIL]: <MailOutlined />,
  [CUSTOMER_PROPERTIES.LOCALE]: <GlobalOutlined />,
  [CUSTOMER_PROPERTIES.CREATED_AT]: <CalendarOutlined />,
  [CUSTOMER_PROPERTIES.PHONE]: null,
  [CUSTOMER_PROPERTIES.NOTE]: <FormOutlined />,
  [CUSTOMER_PROPERTIES.ORDER_VOLUME]:  <EuroSymbolIcon />,
  [CUSTOMER_PROPERTIES.UTM_MEDIUM]:  <DeliveredProcedureOutlined />,
  [CUSTOMER_PROPERTIES.UTM_CAMPAIGN]:  <DeliveredProcedureOutlined />,
  [CUSTOMER_PROPERTIES.RESULT]:  <LinkOutlined />,
  [CUSTOMER_PROPERTIES.SUBSCRIBER]: <CheckCircleOutlined />,
  [CUSTOMER_PROPERTIES.DOI_DATE]: <CalendarOutlined />,
};

const CUSTOMER_PROPERTY_COMPONENTS = {
  [CUSTOMER_PROPERTIES.NAME]: LeadName,
  [CUSTOMER_PROPERTIES.EMAIL]: LeadEmail,
  [CUSTOMER_PROPERTIES.LOCALE]: LeadLocale,
  [CUSTOMER_PROPERTIES.PHONE]: CustomerPhone,
  [CUSTOMER_PROPERTIES.CREATED_AT]: LeadCreatedAt,
  [CUSTOMER_PROPERTIES.STATUS]: CustomerState,
  [CUSTOMER_PROPERTIES.NOTE]: LeadNote,
  [CUSTOMER_PROPERTIES.ORDER_VOLUME]: OrderVolume,
  [CUSTOMER_PROPERTIES.UTM_MEDIUM]: UtmMedium,
  [CUSTOMER_PROPERTIES.UTM_CAMPAIGN]: UtmCampaign,
  [CUSTOMER_PROPERTIES.RESULT]: ResultLink,
  [CUSTOMER_PROPERTIES.SUBSCRIBER]: CustomerSubscriber,
  [CUSTOMER_PROPERTIES.DOI_DATE]: CustomerDoiDate,
};

type ILeadPropertyComponentProps = {
  lead: LeadModel;
  property: string;
  isTable?: boolean;
  showOnePhone?: boolean;
};
export const LeadPropertyComponent = ({
  lead,
  property,
  isTable,
  showOnePhone = false,
}: ILeadPropertyComponentProps) => {
  const Component = CUSTOMER_PROPERTY_COMPONENTS[property];
  if (!Component) return null;

  // @ts-ignore
  return <Component lead={lead} isTable={isTable} showOnePhone={showOnePhone} isCustomer />;
};

type IOrderedLeadPropertiesType = {
  lead: LeadModel;
  showAllProperties?: boolean;
  showFieldName?: boolean;
  showOnePhone?: boolean;
  isTable?: boolean;
  spaceSize?: number;
  grid?: ColProps;
  overrideVisibleProperties?: string[];
  onVisiblePropertiesChange?: () => void;
  classes?: {
    fieldName?: string;
    fieldValue?: string;
  };
};
export const CustomerCardOrderedProperties = React.memo(
  ({
    lead,
    showFieldName = false,
    isTable = false,
    spaceSize = 7,
    grid,
    classes = {},
    showOnePhone = false,
  }: Readonly<IOrderedLeadPropertiesType>) => {


    const  visibleProperties = INITIAL_PROPERTY_VISIBILITY_SETTINGS.shown;
    const presentations =visibleProperties
    //  [
    //   ...visibleProperties,
    //   ...Object.keys(lead?.customer?.styles),
    // ]
      .map((property) => {
        const Presentation = LeadPropertyComponent({
          lead,
          property,
          isTable,
          showOnePhone,
        });
        if (!Presentation) return null;
        const Icon = LEAD_PROPERTY_ICONS[property];
        const fieldWithIcon = (
          <div key={property} className={styles.propertyRow}>
            {Icon && (
              <div
                className={styles.propertyIcon}
                onClick={(e) => e.stopPropagation()}
              >
                {Icon}
              </div>
            )}
            {Presentation && (
              <div
                onClick={(e) => e.stopPropagation()}
                className={classNames(
                  styles.propertyPresentation,
                  classes.fieldValue,
                )}
              >
                {Presentation}
              </div>
            )}
          </div>
        );
        if (!showFieldName) return fieldWithIcon;
        return (
          <Space
            onClick={(e) => e.stopPropagation()}
            direction={'vertical'}
            key={`${lead.id}_${property}`}
          >
            <strong className={classes.fieldName}>
            <F
              id={`pages.lead.fields.${property}`}
              defaultMessage={
                <F
                  {...(CUSTOMER_PROPERTY_COLUMN_SETTINGS[property] || {
                    title: { id: `pages.lead.fields.${property}`, defaultMessage: property },
                  }).title}
                />
              }
            />
            </strong>
            {fieldWithIcon}
          </Space>
        );
      })
      .filter((property) => property);
    let content = presentations;

    if (grid) {
      content = (
        <Row gutter={[16, 16]}>
          {presentations.map((presentation, index) => (
            <Col {...grid} key={index}>
              {presentation}
            </Col>
          ))}
        </Row>
      );
    }
    return (
      <div className={styles.propertiesContainer} style={{ gap: spaceSize }}>
        {content}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      isEqual(prevProps.lead, nextProps.lead) &&
      isEqual(prevProps.overrideVisibleProperties, nextProps.overrideVisibleProperties)
    );
  },
);
